import React from 'react';

// ShapeRenderer — inline SVG renderer for the 'shape' element type.
// Renders at a 100×100 viewBox (or a specific aspect for arrow / line)
// and lets CSS / transform scale up.
//
//   fill                  — fill colour
//   stroke + strokeWidth  — optional outline (colour + width in viewBox units)
//   radius (0–100)        — corner roundness
//
// Corner rounding is geometric — a rounded-corner path for polygons and `rx`
// for the rect — so it's fully independent of the outline. The `.v2-cc-shape`
// svg is overflow:visible (see CSS) so a centred outline stroke isn't clipped.

const POLYGONS = {
  triangle: { viewBox: '0 0 100 100', points: '50,6 96,94 4,94' },
  diamond:  { viewBox: '0 0 100 100', points: '50,5 95,50 50,95 5,50' },
  pentagon: { viewBox: '0 0 100 100', points: '50,5 95,39 78,94 22,94 5,39' },
  hexagon:  { viewBox: '0 0 100 100', points: '27,7 73,7 96,50 73,93 27,93 4,50' },
  star:     { viewBox: '0 0 100 100', points: '50,5 61.8,38.2 96.6,38.2 68.4,59.5 78.2,93.5 50,72.3 21.8,93.5 31.6,59.5 3.4,38.2 38.2,38.2' },
  arrow:    { viewBox: '0 0 120 60',  points: '0,22 70,22 70,6 116,30 70,54 70,38 0,38' },
};

const HEART_PATH = 'M50 86 C20 64 6 42 22 26 C33 15 47 18 50 32 C53 18 67 15 78 26 C94 42 80 64 50 86 Z';

// Build a closed SVG path for a polygon, optionally rounding each corner.
// `radiusPct` (0–100) maps to a max corner radius in viewBox units, clamped
// per-corner to half the shorter adjacent edge so thin spikes (e.g. star
// points) round proportionally rather than collapsing.
const polygonPath = (pointsStr, radiusPct) => {
  const pts = pointsStr.trim().split(/\s+/).map((p) => p.split(',').map(Number));
  const n = pts.length;
  if (!(radiusPct > 0)) {
    return 'M' + pts.map((p) => `${p[0]},${p[1]}`).join(' L') + ' Z';
  }
  const maxR = (radiusPct / 100) * 38;
  let d = '';
  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n];
    const curr = pts[i];
    const next = pts[(i + 1) % n];
    const v1x = prev[0] - curr[0];
    const v1y = prev[1] - curr[1];
    const v2x = next[0] - curr[0];
    const v2y = next[1] - curr[1];
    const l1 = Math.hypot(v1x, v1y) || 1;
    const l2 = Math.hypot(v2x, v2y) || 1;
    const t = Math.min(maxR, l1 / 2, l2 / 2);
    const ax = curr[0] + (v1x / l1) * t;
    const ay = curr[1] + (v1y / l1) * t;
    const bx = curr[0] + (v2x / l2) * t;
    const by = curr[1] + (v2y / l2) * t;
    d += (i === 0 ? 'M' : ' L') + `${ax.toFixed(2)},${ay.toFixed(2)}`;
    d += ` Q${curr[0].toFixed(2)},${curr[1].toFixed(2)} ${bx.toFixed(2)},${by.toFixed(2)}`;
  }
  return d + ' Z';
};

const ShapeRenderer = ({ shape, fill = '#FFFFFF', stroke = null, strokeWidth = 0, radius = 0 }) => {
  const r = Math.max(0, Math.min(radius || 0, 100));
  const sw = stroke && strokeWidth > 0 ? strokeWidth : 0;
  const outline = sw > 0
    ? { stroke, strokeWidth: sw, strokeLinejoin: 'round', strokeLinecap: 'round' }
    : { stroke: 'none' };

  if (shape === 'circle') {
    return (
      <svg className="v2-cc-shape v2-cc-shape-circle" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" aria-hidden>
        <circle cx="50" cy="50" r={50 - sw / 2} fill={fill} {...outline} />
      </svg>
    );
  }

  if (shape == null || shape === 'rect') {
    return (
      <svg className="v2-cc-shape v2-cc-shape-rect" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" aria-hidden>
        <rect x={sw / 2} y={sw / 2} width={100 - sw} height={100 - sw} rx={(r / 100) * 50} fill={fill} {...outline} />
      </svg>
    );
  }

  if (shape === 'line') {
    return (
      <svg className="v2-cc-shape v2-cc-shape-line" viewBox="0 0 120 6" preserveAspectRatio="xMidYMid meet" aria-hidden>
        <rect x="0" y="2" width="120" height="2" rx={(r / 100) * 1} fill={fill} />
      </svg>
    );
  }

  if (shape === 'heart') {
    return (
      <svg className="v2-cc-shape v2-cc-shape-heart" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" aria-hidden>
        <path d={HEART_PATH} fill={fill} {...outline} />
      </svg>
    );
  }

  const poly = POLYGONS[shape];
  if (!poly) return null;
  return (
    <svg className={`v2-cc-shape v2-cc-shape-${shape}`} viewBox={poly.viewBox} preserveAspectRatio="xMidYMid meet" aria-hidden>
      <path d={polygonPath(poly.points, r)} fill={fill} {...outline} />
    </svg>
  );
};

export default ShapeRenderer;
